import { db } from '@/lib/firebase';
import {
  collection,
  getDocs,
  getDoc,
  doc,
  query,
  orderBy,
  limit,
  where,
} from 'firebase/firestore';
import { normalizeGenre } from '@/lib/genres';
import { decodeHtmlEntities } from '@/lib/decode-entities';
import { isVideoUrl } from '@/lib/media-kind';
import type { Playlist, NewRelease } from '@/types/media';
import type { Label } from '@/types/user';
import type { Artist } from '@/types/user';
import type { MarketplaceItem } from '@/types/marketplace';
import type { UpcomingEvent } from '@/types/event';
import type { CommunityPick } from '@/types/community';
import type { TopChart } from '@/types/chart';
import type { RadioStation } from '@/types/radio';

/**
 * Fetch featured artists from Firebase
 * Returns real Breakin' Bread artists
 */

/** Who may edit a work/release — Home's Edit pencil shows for them (and for
 *  admins). Same owner fields the track page checks. */
const ownerOf = (d: any): string =>
  d?.owner?.userId || d?.createdByUid || d?.uploadedBy || d?.createdBy || d?.userId || d?.artist?.createdBy || '';

export async function getFeaturedArtists(): Promise<Artist[]> {
  try {
    const q = query(
      collection(db, 'artists'),
      orderBy('popularity', 'desc'),
      limit(100)
    );
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      console.warn('No artists found in Firebase. Run: npm run seed:breakin-bread');
      return [];
    }

    return snapshot.docs
      .filter(doc => !doc.data().adminHidden && doc.data().hidden !== true) // respect super admin hide toggles (bulk adminHidden + per-item eye toggle `hidden`)
      .map(doc => {
        const d = doc.data();
        // Leave imageUrl/images empty when the artist has no real photo so the
        // home grid falls back to <GenerativeArtistAvatar/> instead of rendering
        // a "300×300" placehold.co square. Was: hardcoded 'placehold.co/300x300.png'.
        const primaryImg = d.imageUrl || d.profilePicture || '';
        const images: string[] = Array.isArray(d.images) && d.images.length > 0
          ? d.images.filter(Boolean)
          : primaryImg ? [primaryImg] : [];
        return {
          id: doc.id,
          name: d.name || 'Unknown Artist',
          genre: normalizeGenre(d.genres?.[0] || d.genre || 'Hip-Hop'),
          imageUrl: primaryImg,
          images,
          dataAiHint: `${d.name} artist`,
          profileLink: `/artists/${d.slug || doc.id}`,
          type: 'Artist' as const,
          dateAdded: d.createdAt?.toDate?.()?.toISOString() || (typeof d.dateAdded === 'string' ? d.dateAdded : new Date().toISOString()),
          popularity: d.popularity || 80,
        };
      }) as Artist[];
  } catch (error) {
    console.error('Error fetching artists:', error);
    return [];
  }
}

/**
 * Fetch featured labels from Firebase
 */
export async function getFeaturedLabels(max = 12): Promise<Label[]> {
  try {
    const q = query(
      collection(db, 'labels'),
      limit(max)
    );
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      console.warn('No labels found in Firebase. Run: npm run seed:breakin-bread');
      return [];
    }

    return snapshot.docs
      .filter(doc => !doc.data().adminHidden && doc.data().hidden !== true)
      .map(doc => {
        const d = doc.data();
        const primaryImg = d.imageUrl || d.logoUrl || d.logo || d.thumbnail || d.coverArtUrl || '';
        const images: string[] = Array.isArray(d.images) && d.images.length > 0
          ? d.images.filter(Boolean)
          : primaryImg ? [primaryImg] : [];
        return {
          id: doc.id,
          name: d.name || 'Unknown Label',
          genre: normalizeGenre(d.genres?.[0] || 'Hip-Hop'),
          imageUrl: primaryImg,
          images,
          dataAiHint: `${d.name} record label`,
          profileLink: `/labels/${d.slug || doc.id}`,
          type: 'Label' as const,
          dateAdded: d.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
          popularity: d.popularity || 90,
        };
      }) as Label[];
  } catch (error) {
    console.error('Error fetching labels:', error);
    return [];
  }
}

/**
 * Fetch demo content: beats, demo compilations, and instrumentals
 * Queries works (type: Beat/Demo/Beat Pack/Instrumental) + demo-type releases
 */
export async function getDemoContent(): Promise<NewRelease[]> {
  const mapWork = (d: import('firebase/firestore').QueryDocumentSnapshot) => {
    const data = d.data();
    // Empty when missing → home grid uses GenerativeArtistAvatar instead of a
    // grey 300×300 placeholder. Cover-art enrichment below still works because
    // we now check `!imageUrl` rather than comparing to the placeholder string.
    const primaryImg = data.coverArtUrl || data.imageUrl || '';
    const images: string[] = Array.isArray(data.images) && data.images.length > 0
      ? data.images.filter(Boolean)
      : primaryImg ? [primaryImg] : [];
    return {
      id: d.id,
      title: data.title || 'Untitled',
      artist: data.artist?.name || data.artistName || 'Unknown Artist',
      imageUrl: primaryImg,
      images,
      type: data.type || 'Beat',
      genre: normalizeGenre(data.genres?.[0] || data.genre || 'Hip-Hop'),
      status: data.status === 'published' ? 'Released' : 'Unreleased',
      // The AI mark on Home's cards needs these (a release moved from
      // Releases into Demos lost its mark — Gordon 2026-10-31).
      aiDisclosure: data.aiDisclosure || null,
      ownerUid: ownerOf(data),
      aiLevel: data.aiLevel || null,
      dateAdded: data.createdAt?.toDate?.()?.toISOString?.() || new Date().toISOString(),
      popularity: data.popularity || 50,
    } as NewRelease;
  };

  const mapRelease = (d: import('firebase/firestore').QueryDocumentSnapshot) => {
    const data = d.data();
    const rawDate = data.releaseDate;
    const primaryImg = data.coverArtUrl || data.imageUrl || (Array.isArray(data.images) && data.images[0]) || '';
    const images: string[] = Array.isArray(data.images) && data.images.length > 0
      ? data.images.filter(Boolean)
      : primaryImg ? [primaryImg] : [];
    return {
      id: d.id,
      title: data.title || 'Untitled',
      artist: data.artist?.name || data.artistName || 'Unknown Artist',
      imageUrl: primaryImg,
      images,
      releaseType: (data.releaseType?.toLowerCase() === 'demo' ? 'Demo' : data.releaseType) || 'Demo',
      aiDisclosure: data.aiDisclosure || null,
      ownerUid: ownerOf(data),
      aiLevel: data.aiLevel || null,
      genre: normalizeGenre(data.genres?.[0] || data.genre || 'Hip-Hop'),
      status: data.status === 'released' ? 'Released' : 'Unreleased',
      dateAdded:
        rawDate?.toDate?.()?.toISOString?.() ||
        (typeof rawDate === 'string' ? rawDate : null) ||
        data.createdAt?.toDate?.()?.toISOString?.() ||
        new Date().toISOString(),
      popularity: data.popularity || 50,
    } as NewRelease;
  };

  const demoTypes = ['Beat', 'Demo', 'Beat Pack', 'Instrumental'];
  // 'Demo' covers all NUFF BEATS / Aeons compilations (updated via data fix).
  // No plain 'compilation' catch-all: it put the released album "Cuts &
  // Slices of Breakin Bread" in Demos (Gordon 2026-10-31) — a demo
  // compilation is typed 'Demo Compilation'.
  const demoReleaseTypes = ['Demo', 'demo', 'Demo Compilation', 'demo compilation', 'Beat', 'beat', 'Beat Pack', 'beat pack'];

  const safeFetch = async (q: import('firebase/firestore').Query) => {
    try { return await getDocs(q); }
    catch { return null; }
  };

  try {
    const [worksSnap, releasesSnap] = await Promise.all([
      safeFetch(query(collection(db, 'works'), where('type', 'in', demoTypes), orderBy('createdAt', 'desc'), limit(100)))
        .then(s => s ?? safeFetch(query(collection(db, 'works'), where('type', 'in', demoTypes), limit(100)))),
      safeFetch(query(collection(db, 'releases'), where('releaseType', 'in', demoReleaseTypes), orderBy('createdAt', 'desc'), limit(50)))
        .then(s => s ?? safeFetch(query(collection(db, 'releases'), where('releaseType', 'in', demoReleaseTypes), limit(50)))),
    ]);

    // Build workId → coverArtUrl reverse map from releases (same pattern as MediaVault)
    // so works that lack their own coverArtUrl get the album art from their parent release
    const relCoverMap = new Map<string, string>();
    releasesSnap?.docs.forEach(d => {
      const data = d.data();
      const art = data.coverArtUrl || (Array.isArray(data.images) && data.images[0]) || '';
      if (art && Array.isArray(data.tracks)) {
        data.tracks.forEach((t: any) => {
          const wid = t.workId || t.id;
          if (wid && !relCoverMap.has(wid)) relCoverMap.set(wid, art);
        });
      }
    });

    const seen = new Set<string>();
    const merged: NewRelease[] = [];

    // Releases first (compilations as cards) — only those with at least one
    // playable track (skip AWAL-style metadata-only imports).
    const releaseItems = (releasesSnap?.docs ?? [])
      .filter(d => !d.data().adminHidden && d.data().hidden !== true && releaseIsPlayable(d.data()))
      .map(mapRelease);
    // Works filter: only respect explicit admin flags, NOT an implicit
    // audioUrl check. Many demo/beat works store streamable URLs under
    // mp3Url, sourceUrl, hiResUrl, etc. — a naive audioUrl-only check
    // wrongly hid them all. The Hide audio-less admin action is what
    // sets `incomplete` durably; everything else passes through.
    const workItems = (worksSnap?.docs ?? [])
      .filter(d => {
        const data = d.data();
        if (data.adminHidden) return false;
        if (data.hidden === true) return false;
        if (data.incomplete === true) return false;
        // Unlisted / private demos (e.g. a collab demo saved to the Vault)
        // are link-only — never on the public home row.
        const privacy = String(data.privacy || '').toLowerCase();
        if (privacy === 'unlisted' || privacy === 'private') return false;
        // Drop audio works that have no playable URL — Gordon: "hide all
        // tracks without audio files." Video / YouTube works pass through.
        if (data.type !== 'video' && !data.videoUrl && !data.youtubeId) {
          const audio = data.audioUrl || data.mp3Url || data.hiResUrl || data.sourceUrl || data.fileUrl || '';
          if (!audio) return false;
        }
        return true;
      })
      .map(d => {
        const item = mapWork(d);
        const fallback = relCoverMap.get(d.id) || '';
        // Was: compared to the literal placehold.co URL. Now mapWork returns
        // '' when there's no real cover, so we enrich on empty/placeholder.
        if (fallback && !(item as any).imageUrl) {
          (item as any).imageUrl = fallback;
          (item as any).images = [fallback];
        }
        return item;
      });

    for (const item of [...releaseItems, ...workItems]) {
      if (!seen.has(item.id)) { seen.add(item.id); merged.push(item); }
    }
    return merged.sort((a: any, b: any) => new Date(b.dateAdded || 0).getTime() - new Date(a.dateAdded || 0).getTime());
  } catch (error) {
    console.error('Error fetching demo content:', error);
    return [];
  }
}

/**
 * Fetch new releases from Firebase
 * Maps the 'releases' collection to NewRelease type
 */
/**
 * A release is "playable" — i.e. allowed on the public home rail.
 *
 * BB stores per-track audio in separate `works/{workId}` docs. The embedded
 * release.tracks[] entries don't always carry the audioUrl, so a naive
 * "every track has audioUrl?" check would wrongly hide healthy releases.
 *
 * To avoid that whole class of false-positives, we ONLY filter releases that
 * an admin explicitly marked `incomplete: true` via Super Admin → AWAL Import
 * → Hide audio-less releases (or by clearing it manually). The implicit
 * track-by-track audio check is gone — the explicit flag is the source of
 * truth, and the AWAL import endpoint is what sets it. adminHidden is still
 * honored at the call sites.
 */
export function releaseIsPlayable(data: any): boolean {
  if (!data) return false;
  if (data.incomplete === true) return false;
  // Defence-in-depth: if the release has a tracks[] array but NONE of the
  // tracks have a direct audio URL (audioUrl or mp3Url), the release is
  // metadata-only and should be hidden. The previous workId-based check let
  // AWAL imports through because the catalog-import endpoint creates work
  // docs (and writes workId onto tracks) even though the works themselves are
  // audio-less. The right cut is the literal audio field:
  //
  //   • Bulk-upload / Album EP Upload writes `mp3Url` onto each track.
  //   • Single-upload writes `audioUrl` directly.
  //   • Catalog-import (AWAL) writes ONLY `{ trackNumber, title, isrc, workId }`
  //     — no audio field, so it falls through the filter.
  //
  // workId alone is insufficient because it can point at an equally audio-less
  // work doc. If a release truly stores audio on works behind workIds, an
  // admin can flip `incomplete: false` (the explicit short-circuit above) to
  // force visibility.
  if (Array.isArray(data.tracks) && data.tracks.length > 0) {
    const anyPlayable = data.tracks.some((t: any) => {
      const audio = typeof t?.audioUrl === 'string' ? t.audioUrl.trim() : '';
      const mp3 = typeof t?.mp3Url === 'string' ? t.mp3Url.trim() : '';
      return audio.length > 0 || mp3.length > 0;
    });
    if (!anyPlayable) return false;
  }
  return true;
}

// The newest 200 releases, shared for a minute by every caller on the page —
// the New Releases rail and the visitor welcome read the same docs, and
// fetching them twice doubles the Firestore reads for every first visit.
let recentReleasesCache: { at: number; docs: Promise<import('firebase/firestore').QueryDocumentSnapshot[]> } | null = null;
function getRecentReleaseDocs() {
  if (!recentReleasesCache || Date.now() - recentReleasesCache.at > 60_000) {
    const docs = getDocs(query(collection(db, 'releases'), orderBy('createdAt', 'desc'), limit(200))).then(s => s.docs);
    docs.catch(() => { recentReleasesCache = null; });
    recentReleasesCache = { at: Date.now(), docs };
  }
  return recentReleasesCache.docs;
}

export interface WelcomeTrack {
  id: string;
  releaseId: string;
  title: string;
  artist: string;
  imageUrl: string;
  audioUrl: string;
  genre?: string;
}

/** Playable, non-explicit tracks from visible releases, shuffled — the pool
 *  the visitor welcome cues into the player and shows on its Listen tab. */
export async function getWelcomeTracks(max = 24): Promise<WelcomeTrack[]> {
  try {
    const out: WelcomeTrack[] = [];
    for (const snap of await getRecentReleaseDocs()) {
      const r = snap.data() as any;
      if (r.adminHidden || r.hidden === true || r.explicit === true || !releaseIsPlayable(r)) continue;
      const art = r.coverArtUrl || r.imageUrl || (Array.isArray(r.images) && r.images[0]) || '';
      const tracks: any[] = Array.isArray(r.tracks) ? r.tracks : [];
      tracks.forEach((t, i) => {
        const audioUrl = String(t?.mp3Url || t?.audioUrl || '').trim();
        if (!audioUrl || t?.explicit === true) return;
        out.push({
          id: t.workId || t.id || `${snap.id}-${t.trackNumber ?? i + 1}`,
          releaseId: snap.id,
          title: t.title || r.title || 'Track',
          artist: t.artistName || r.artistName || r.artist?.name || '',
          imageUrl: art,
          audioUrl,
          genre: normalizeGenre(r.genres?.[0] || r.genre || ''),
        });
      });
    }
    for (let i = out.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [out[i], out[j]] = [out[j], out[i]];
    }
    // One track per release first, so the pool isn't one album.
    const seen = new Set<string>();
    const spread = [...out.filter(t => !seen.has(t.releaseId) && seen.add(t.releaseId)), ...out];
    return [...new Map(spread.map(t => [t.id, t])).values()].slice(0, max);
  } catch (error) {
    console.error('Error fetching welcome tracks:', error);
    return [];
  }
}

/** Every visible, playable release — demos and beat packs included — for the
 *  visitor header strip (Gordon 2026-10-29: "show all releases in the favorites
 *  for visitors"). Reads the shared cached query, no extra reads. */
/** Releases for the record crate (Gordon 2026-11-02): cover, artist and the
 *  tracks that have audio, newest first — the same shared releases read. */
export interface CrateRelease { id: string; title: string; artist: string; imageUrl: string; tracks: { id: string; title: string; audioUrl: string }[] }
export async function getCrateReleases(): Promise<CrateRelease[]> {
  const out: CrateRelease[] = [];
  for (const snap of await getRecentReleaseDocs()) {
    const r = snap.data() as any;
    if (r.adminHidden || r.hidden === true || !releaseIsPlayable(r)) continue;
    const tracks = (Array.isArray(r.tracks) ? r.tracks : [])
      .filter((t: any) => t && (t.mp3Url || t.audioUrl))
      .map((t: any, i: number) => ({ id: t.workId || t.id || `${snap.id}-${i}`, title: t.title || r.title || 'Untitled', audioUrl: t.mp3Url || t.audioUrl }));
    out.push({
      id: snap.id,
      title: r.title || 'Untitled',
      artist: r.artistName || r.artist?.name || '',
      imageUrl: r.coverArtUrl || r.imageUrl || (Array.isArray(r.images) && r.images[0]) || '',
      tracks,
    });
  }
  return out;
}

export async function getVisibleReleases(): Promise<{ id: string; title: string; imageUrl: string }[]> {
  const out: { id: string; title: string; imageUrl: string }[] = [];
  for (const snap of await getRecentReleaseDocs()) {
    const r = snap.data() as any;
    if (r.adminHidden || r.hidden === true || !releaseIsPlayable(r)) continue;
    out.push({ id: snap.id, title: r.title || 'Untitled', imageUrl: r.coverArtUrl || r.imageUrl || (Array.isArray(r.images) && r.images[0]) || '' });
  }
  return out;
}

/** Recent visible releases with who made them — for the header message board,
 *  which narrows them to the artists / people a user follows. Same cached query. */
export async function getBoardReleases(): Promise<{ id: string; title: string; artist: string; artistId: string; ownerUid: string; imageUrl: string }[]> {
  const out: { id: string; title: string; artist: string; artistId: string; ownerUid: string; imageUrl: string }[] = [];
  for (const snap of await getRecentReleaseDocs()) {
    const r = snap.data() as any;
    if (r.adminHidden || r.hidden === true || !releaseIsPlayable(r)) continue;
    out.push({
      id: snap.id,
      title: r.title || 'Untitled',
      artist: r.artistName || r.artist?.name || (typeof r.artist === 'string' ? r.artist : '') || '',
      artistId: r.artistId || '',
      ownerUid: r.ownerUid || r.owner?.userId || '',
      imageUrl: r.coverArtUrl || r.imageUrl || (Array.isArray(r.images) && r.images[0]) || '',
    });
  }
  return out;
}

/** Graphics for the /launch manifesto slideshow (Gordon 2026-10-29: "slideshow
 *  style graphics taken from the users graphics media vault"). Only Media
 *  Vault images their owners marked Public — never a Private upload. While
 *  there are fewer than six, release cover art fills in (artists' gallery
 *  photos were tried first, but they are personal photos, not graphics). */
export async function getPublicGraphics(max = 24): Promise<string[]> {
  const IMAGE_TYPES = ['Image', 'Image/Artwork', 'Artwork', 'Brand', 'Photo', 'image', 'image/artwork', 'artwork', 'brand', 'photo'];
  const out = new Set<string>();
  const keep = (u: unknown) => { if (typeof u === 'string' && u && !u.startsWith('data:') && !isVideoUrl(u)) out.add(u); };
  try {
    const snap = await getDocs(query(collection(db, 'works'), where('type', 'in', IMAGE_TYPES), limit(200)));
    snap.forEach(d => {
      const w = d.data() as any;
      if (String(w.privacy || '').toLowerCase() !== 'public' || w.adminHidden || w.deleted) return;
      keep(w.imageUrl || w.coverArtUrl || w.fileUrl);
    });
  } catch { /* fall through to the gallery fill */ }
  if (out.size < 6) {
    try {
      (await getVisibleReleases()).forEach(r => keep(r.imageUrl));
    } catch { /* nothing to fill with */ }
  }
  return [...out].slice(0, max);
}

/** The newest visible, playable, non-explicit releases with cover art — the
 *  hero's "New release" slides. Reads the shared cached query, no extra reads. */
export async function getHeroReleases(max = 4): Promise<{ id: string; title: string; artist: string; imageUrl: string }[]> {
  const out: { id: string; title: string; artist: string; imageUrl: string }[] = [];
  for (const snap of await getRecentReleaseDocs()) {
    const r = snap.data() as any;
    if (r.adminHidden || r.hidden === true || r.explicit === true || !releaseIsPlayable(r)) continue;
    const imageUrl = r.coverArtUrl || r.imageUrl || (Array.isArray(r.images) && r.images[0]) || '';
    if (!imageUrl) continue;
    out.push({ id: snap.id, title: r.title || 'New release', artist: r.artistName || r.artist?.name || '', imageUrl });
    if (out.length >= max) break;
  }
  return out;
}

/** Creator-thumbnail fallback: sync uploads rarely carry their own artwork,
 *  and the generative-initials tile reads as broken ("A1", "A6"…). When a
 *  track has no imageUrl, show the creator instead — artist doc image first
 *  (by id, then by name), then the posting user's profile image. The returned
 *  resolver caches per creator, so a batch from one artist costs one lookup.
 *  Shared by the home Sync section and the /synchronization catalog list. */
export function makeCreatorThumbResolver() {
  const cache = new Map<string, string>();
  return async (t: { artistId?: string; artistName?: string; postedBy?: string }): Promise<string> => {
    const key = `${t.artistId || ''}|${t.artistName || ''}|${t.postedBy || ''}`;
    if (key === '||') return '';
    if (cache.has(key)) return cache.get(key)!;
    let url = '';
    try {
      if (t.artistId) {
        const a = await getDoc(doc(db, 'artists', t.artistId));
        if (a.exists()) url = (a.data() as any).imageUrl || '';
      }
      if (!url && t.artistName) {
        const s = await getDocs(query(collection(db, 'artists'), where('name', '==', t.artistName), limit(1)));
        if (!s.empty) url = (s.docs[0].data() as any).imageUrl || '';
      }
      if (!url && t.postedBy) {
        const u = await getDoc(doc(db, 'users', t.postedBy));
        if (u.exists()) {
          const d = u.data() as any;
          url = d.imageUrl || d.photoURL || '';
        }
      }
    } catch { /* fallback stays '' → generative avatar */ }
    cache.set(key, url);
    return url;
  };
}

/** Sync-catalog highlights for the home page. Reads a handful of the newest
 *  syncTracks and maps them to playable home cards. Sold-exclusive tracks the
 *  maker chose to hide are dropped. */
export async function getSyncHighlights(maxItems: number = 15): Promise<any[]> {
  try {
    const snapshot = await getDocs(collection(db, 'syncTracks'));
    const rows = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as any));
    const picked = rows
      .filter(t => !(t.exclusiveSold && (t.postSaleVisibility ?? 'greyout') === 'hidden'))
      .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
      .slice(0, maxItems);

    const creatorThumb = makeCreatorThumbResolver();

    return await Promise.all(picked.map(async t => ({
      id: t.id,
      workId: t.workId || '',
      title: t.title || 'Untitled',
      artist: t.artistName || '',
      imageUrl: t.imageUrl || await creatorThumb(t),
      audioUrl: t.audioUrl || '',
      genre: t.genre || '',
      usageTypes: t.usageTypes || [],
      dataAiHint: 'sync music',
    })));
  } catch (error) {
    console.error('Error fetching sync highlights:', error);
    return [];
  }
}

export async function getNewReleases(): Promise<NewRelease[]> {
  const mapDoc = (doc: import('firebase/firestore').QueryDocumentSnapshot) => {
    const data = doc.data();
    // Normalise date — releaseDate may be a Timestamp, ISO string, or missing
    const rawDate = data.releaseDate;
    const dateAdded: string =
      rawDate?.toDate?.()?.toISOString?.() ||
      (typeof rawDate === 'string' ? rawDate : null) ||
      data.createdAt?.toDate?.()?.toISOString?.() ||
      new Date().toISOString();
    // Drop the placehold.co fallback — when there's no real artwork, leave
    // imageUrl empty so the home card can render the generative avatar
    // (procedural visual based on the title hash) instead of a grey
    // placeholder. Same for images[] — pass an empty array, not [placeholder].
    const realPrimary = data.coverArtUrl || data.imageUrl || (Array.isArray(data.images) && data.images[0]) || '';
    const realImages: string[] = Array.isArray(data.images) && data.images.length > 0
      ? data.images.filter(Boolean)
      : (realPrimary ? [realPrimary] : []);
    return {
      id: doc.id,
      title: data.title || 'Untitled',
      artist: data.artist?.name || data.artistName || 'Unknown Artist',
      imageUrl: realPrimary,
      images: realImages,
      explicit: data.explicit === true,
      aiDisclosure: data.aiDisclosure || null,
      ownerUid: ownerOf(data),
      aiLevel: data.aiLevel || null,
      dataAiHint: `${data.title} album cover`,
      type: (
        { album: 'Album', ep: 'EP', single: 'Single', mixtape: 'Mixtape', compilation: 'Compilation', 'live album': 'Live Album', live_album: 'Live Album' } as Record<string, string>
      )[(data.releaseType || '').toLowerCase()] || 'Single',
      genre: normalizeGenre(data.genres?.[0] || data.genre || 'Hip-Hop'),
      status: data.status === 'released' ? 'Released' : 'Unreleased',
      dateAdded,
      popularity: data.popularity || 80,
    } as NewRelease;
  };

  // Reusable filter: drop releases that have zero playable tracks (the AWAL
  // imports with metadata but no audio) and respect adminHidden as before.
  // Demo/beat-type releases live in the home "Demos" section (getDemoContent)
  // — changing a release's type to Demo moves it there instead of listing it
  // in both places (Gordon: "if you change a release to a demo then it should
  // not be in releases but in demos").
  const DEMO_ONLY_TYPES = new Set(['demo', 'demos', 'demo compilation', 'beat', 'beat pack']);
  const isVisible = (snap: import('firebase/firestore').QueryDocumentSnapshot) => {
    const d = snap.data();
    // Some older docs carry the type in `type` instead of `releaseType`
    // (a since-fixed quick-edit bug wrote there) — check both.
    const rt = String(d.releaseType || '').trim().toLowerCase();
    const t = String(d.type || '').trim().toLowerCase();
    if (DEMO_ONLY_TYPES.has(rt) || DEMO_ONLY_TYPES.has(t)) return false;
    return !d.adminHidden && d.hidden !== true && releaseIsPlayable(d);
  };

  try {
    // Primary: ordered by createdAt (all releases have this)
    const recent = await getRecentReleaseDocs();
    if (recent.length > 0) return recent.filter(isVisible).map(mapDoc);

    // Fallback: unordered scan (covers older docs without createdAt)
    const fallbackQ = query(collection(db, 'releases'), limit(200));
    const fallbackSnap = await getDocs(fallbackQ);
    if (!fallbackSnap.empty) return fallbackSnap.docs.filter(isVisible).map(mapDoc);

    console.warn('No releases found in Firebase.');
    return [];
  } catch (error) {
    console.error('Error fetching releases:', error);
    // Last resort: unordered, no index needed
    try {
      const snap = await getDocs(query(collection(db, 'releases'), limit(200)));
      return snap.docs.filter(isVisible).map(mapDoc);
    } catch { return []; }
  }
}

/**
 * Fetch popular playlists from Firebase
 * Note: Playlists collection may not exist yet - returns empty array
 */
export async function getPopularPlaylists(max = 8): Promise<Playlist[]> {
  try {
    // Public playlists only, sorted here: orderBy('popularity') silently
    // dropped every playlist without that field (the Playlists page showed 0
    // of 7), and private playlists must never be listed.
    const q = query(
      collection(db, 'playlists'),
      where('visibility', '==', 'public'),
      limit(Math.max(max, 50))
    );
    const snapshot = await getDocs(q);
    const docs = [...snapshot.docs]
      .sort((a, b) => (b.data().popularity || 0) - (a.data().popularity || 0))
      .slice(0, max);

    return docs.map(doc => ({
      id: doc.id,
      title: doc.data().title || 'Untitled Playlist',
      creator: doc.data().creator || 'BB Curators',
      imageUrl: doc.data().imageUrl || doc.data().coverImageUrl || (doc.data().coverImages || [])[0] || '',
      dataAiHint: doc.data().title + ' playlist',
      type: 'Playlist' as const,
      trackCount: doc.data().trackCount || 0,
      status: doc.data().status || 'Published',
      dateAdded: doc.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
      popularity: doc.data().popularity || 70,
    })) as Playlist[];
  } catch (error) {
    // Playlists collection may not exist - this is OK
    console.log('Playlists not available yet');
    return [];
  }
}

/**
 * Fetch marketplace items from Firebase
 */
export async function getMarketplaceItems(): Promise<MarketplaceItem[]> {
  try {
    const q = query(
      collection(db, 'item'),
      where('status', '==', 'active'),
      limit(100)
    );
    const snapshot = await getDocs(q);

    return snapshot.docs
      .filter(doc => !doc.data().adminHidden && doc.data().hidden !== true)
      .map(doc => {
        const data = doc.data();
        const primaryImg = (typeof data.media?.[0] === 'string' ? data.media[0] : data.media?.[0]?.url) || data.imageUrl || '';
        const mediaImages: string[] = Array.isArray(data.media)
          ? data.media.map((m: any) => typeof m === 'string' ? m : m?.url || '').filter(Boolean)
          : [];
        const images: string[] = mediaImages.length > 0 ? mediaImages : primaryImg ? [primaryImg] : [];
        return {
          id: doc.id,
          title: data.title || 'Untitled Item',
          seller: data.seller?.name || "Breakin' Bread",
          price: data.price || data.basePrice || 0,
          imageUrl: primaryImg,
          images,
          dataAiHint: 'merchandise',
          type: 'Product' as const,
          category: data.category?.name || 'Merchandise',
          dateAdded: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
          popularity: data.popularity || 70,
        };
      }) as unknown as MarketplaceItem[];
  } catch (error) {
    console.error('Error fetching marketplace items:', error);
    return [];
  }
}

/**
 * Fetch upcoming events from Firebase
 * Note: Events collection may not exist yet
 */
export async function getUpcomingEvents(): Promise<UpcomingEvent[]> {
  try {
    const q = query(
      collection(db, 'events'),
      orderBy('date', 'asc'),
      limit(100)
    );
    const snapshot = await getDocs(q);

    // Client-side filter — drafts, past events, admin-hidden, and explicitly
    // cancelled docs all stay out of the public "Upcoming Events" rail. Done
    // here instead of as Firestore `where(...)` clauses so a missing
    // composite index can't silently drop the whole rail; older docs without
    // a `status` field default to "published" so the home page doesn't go
    // empty after a schema migration.
    // Gordon's report: "don't know why there's a bunch of events in the home
    // page when there is only one event in backend in draft mode." Prior
    // implementation had no status / adminHidden / date filter at all, so
    // every event doc (including the draft) and any legacy / test event
    // surfaced.
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const todayMs = todayStart.getTime();

    const visible = snapshot.docs.filter(doc => {
      const d = doc.data();
      const status = String(d.status || 'published').toLowerCase();
      if (status === 'draft' || status === 'cancelled' || status === 'archived' || status === 'private') return false;
      if (d.adminHidden === true) return false;
      if (d.hidden === true) return false;
      // Scraped events stay out — per Gordon: "Events is to create and sell
      // for BB content creators or tour managers, booking companies, that
      // want to login and post." The AA scraper used to populate this
      // collection from news articles; we still keep the old rows in the
      // DB for reference but never surface them on the public rail.
      const source = String(d.source || '').toLowerCase();
      if (source === 'aa-scraper' || source.startsWith('scraper:') || source.startsWith('industry-rss:')) return false;
      if (d.postedBy === 'system' || d.createdBy === 'system') return false;
      // Date filter — allow events with no date (TBA) through, drop everything
      // that has a date strictly before today (00:00 local).
      const rawDate = d.date;
      const eventMs =
        rawDate?.toDate?.()?.getTime?.() ??
        (typeof rawDate === 'string' ? Date.parse(rawDate) : NaN);
      if (Number.isFinite(eventMs) && eventMs < todayMs) return false;
      return true;
    });

    return visible.map(doc => {
      const d = doc.data();
      const primaryImg = d.imageUrl || '';
      const images: string[] = Array.isArray(d.images) && d.images.length > 0
        ? d.images.filter(Boolean)
        : primaryImg ? [primaryImg] : [];
      return {
        id: doc.id,
        title: d.title || 'Untitled Event',
        date: d.date?.toDate?.()?.toISOString()?.split('T')[0] || new Date().toISOString().split('T')[0],
        location: d.location || 'TBA',
        imageUrl: primaryImg,
        images,
        dataAiHint: d.title + ' event',
        type: d.type || 'Concert',
        popularity: d.popularity || 70,
        dateAdded: d.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
      };
    }) as UpcomingEvent[];
  } catch (error) {
    // Events collection may not exist yet
    console.log('Events not available yet');
    return [];
  }
}

/**
 * Fetch community picks from Firebase
 */
export async function getCommunityPicks(): Promise<CommunityPick[]> {
  try {
    const q = query(
      collection(db, 'communityPicks'),
      limit(6)
    );
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      title: doc.data().title || 'Community Pick',
      submitter: doc.data().submitter || 'Community',
      imageUrl: doc.data().imageUrl || '',
      dataAiHint: doc.data().title || 'community pick',
      type: doc.data().type || 'Track',
      genre: normalizeGenre(doc.data().genre || 'Hip-Hop'),
      status: doc.data().status || 'Published',
      dateAdded: doc.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
      popularity: doc.data().popularity || 70,
    })) as CommunityPick[];
  } catch (error) {
    // Community picks collection may not exist yet
    console.log('Community picks not available yet');
    return [];
  }
}

/**
 * Fetch top chart - most played songs ordered by popularity
 */
export async function getTopCharts(): Promise<TopChart[]> {
  // Charts = individual TRACKS ranked by real play events (Gordon: "individual
  // tracks. most and least played"). The dedicated /charts page owns the full
  // most/least listing; this is the home teaser (most-played). Falls back to
  // releases-by-popularity so the rail never goes empty when play data is thin.
  try {
    const evSnap = await getDocs(query(
      collection(db, 'mediaEvents'),
      where('entityType', '==', 'track'),
      limit(1000),
    ));
    const counts = new Map<string, number>();
    evSnap.docs.forEach(d => {
      const ev: any = d.data();
      if (ev.type === 'play' && ev.entityId) counts.set(ev.entityId, (counts.get(ev.entityId) || 0) + 1);
    });
    const topIds = [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 20).map(([id]) => id);
    if (topIds.length) {
      // Batch-fetch the works + all releases (for the workId→coverArt fallback,
      // per the recurring-thumbnail rule) in parallel.
      const worksById = new Map<string, any>();
      const relCover = new Map<string, string>();
      const batches = Array.from({ length: Math.ceil(topIds.length / 10) }, (_, i) => topIds.slice(i * 10, i * 10 + 10));
      await Promise.all([
        ...batches.map(async (b) => {
          try {
            const s = await getDocs(query(collection(db, 'works'), where('__name__', 'in', b)));
            s.docs.forEach(d => worksById.set(d.id, { id: d.id, ...d.data() }));
          } catch { /* non-fatal */ }
        }),
        (async () => {
          try {
            const rs = await getDocs(collection(db, 'releases'));
            rs.docs.forEach(d => {
              const r: any = d.data();
              if (r.coverArtUrl && Array.isArray(r.tracks)) {
                r.tracks.forEach((t: any) => {
                  const wid = t.workId || t.id;
                  if (wid && !relCover.has(wid)) relCover.set(wid, r.coverArtUrl);
                });
              }
            });
          } catch { /* non-fatal */ }
        })(),
      ]);
      const cards = topIds.map(id => {
        const w = worksById.get(id);
        if (!w) return null;
        const img = w.coverArtUrl || w.imageUrl || relCover.get(id) || relCover.get(w.workId) || '';
        return {
          id,
          title: w.title || 'Untitled',
          artist: w.artist?.name || w.artistName || w.artist || 'Unknown Artist',
          imageUrl: img,
          images: img ? [img] : [],
          explicit: w.explicit === true,
          aiDisclosure: w.aiDisclosure || null,
          ownerUid: ownerOf(w),
          aiLevel: w.aiLevel || null,
          dataAiHint: 'chart track',
          type: 'Single',
          genre: normalizeGenre(w.genres?.[0] || w.genre || 'Hip-Hop'),
          genreTags: (w.genres || []).map(normalizeGenre),
          status: w.status === 'released' || w.status === 'Published' ? 'Released' : 'Unreleased',
          description: w.artist?.name || w.artistName || '',
          dateAdded: w.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
          popularity: counts.get(id) || 0,
          audioUrl: w.audioUrl || w.mp3Url || '',
          _isTrackChart: true,
        };
      }).filter(Boolean) as any as TopChart[];
      if (cards.length) return cards;
    }
  } catch (error) {
    console.warn('getTopCharts play-ranking failed, falling back to releases:', error);
  }

  // Fallback: top releases by popularity.
  try {
    const q = query(
      collection(db, 'releases'),
      orderBy('popularity', 'desc'),
      limit(20)
    );
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => {
      const data = doc.data();
      const primaryImg = data.coverArtUrl || data.imageUrl || '';
      const images: string[] = Array.isArray(data.images) && data.images.length > 0
        ? data.images.filter(Boolean)
        : primaryImg ? [primaryImg] : [];
      return {
        id: doc.id,
        title: data.title || 'Untitled',
        artist: data.artist?.name || data.artistName || 'Unknown Artist',
        imageUrl: primaryImg,
        images,
        explicit: data.explicit === true,
      aiDisclosure: data.aiDisclosure || null,
      ownerUid: ownerOf(data),
      aiLevel: data.aiLevel || null,
        dataAiHint: `${data.title} song`,
        type: ({ album: 'Album', ep: 'EP', single: 'Single', mixtape: 'Mixtape', compilation: 'Compilation', 'live album': 'Live Album', live_album: 'Live Album' } as Record<string, string>)[(data.releaseType || '').toLowerCase()] || 'Single',
        genre: normalizeGenre(data.genres?.[0] || 'Hip-Hop'),
        genreTags: (data.genres || []).map(normalizeGenre),
        status: data.status === 'released' ? 'Released' : 'Unreleased',
        description: data.artist?.name || data.artistName || '',
        dateAdded: data.releaseDate?.toDate?.()?.toISOString() || (typeof data.releaseDate === 'string' ? data.releaseDate : new Date().toISOString()),
        popularity: data.popularity || 0,
      };
    }) as TopChart[];
  } catch (error) {
    console.error('Error fetching top chart:', error);
    return [];
  }
}

/**
 * Fetch radio stations from Firebase
 */
export async function getRadioStations(): Promise<RadioStation[]> {
  try {
    const q = query(
      collection(db, 'radioStations'),
      limit(8)
    );
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      title: doc.data().title || 'Radio Station',
      description: doc.data().description || '',
      imageUrl: doc.data().imageUrl || '',
      dataAiHint: doc.data().title + ' radio',
      type: 'Radio' as const,
      genre: normalizeGenre(doc.data().genre || 'Hip-Hop'),
      status: doc.data().status || 'Live',
      isOn: doc.data().isOn || false,
      dateAdded: doc.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
      popularity: doc.data().popularity || 70,
    })) as RadioStation[];
  } catch (error) {
    // Radio stations collection may not exist yet
    console.log('Radio stations not available yet');
    return [];
  }
}

/**
 * Fetch news articles from Firebase
 * Includes articles regardless of status so newly published ones appear immediately.
 * Sorts by publishedAt → createdAt → 0 to handle legacy docs missing publishedAt.
 */
// Slim summaries (no article bodies) from the cached server feed — the only
// way the browser should read news (full articles averaged 9 KB each; the
// hero and header board used to pull up to 180 of them: PageSpeed 2026-10-31).
// One shared request per minute for the Home row, the hero and the board.
let newsShared: { at: number; p: Promise<any[]> } | null = null;
export async function getNewsArticles(_maxItems: number = 100) {
  if (newsShared && Date.now() - newsShared.at < 60_000) return newsShared.p;
  const p = (async () => {
    const response = await fetch('/api/home/news');
    if (!response.ok) throw new Error('News temporarily unavailable');
    const data = await response.json();
    if (!Array.isArray(data.articles)) throw new Error('Invalid news feed');
    return data.articles.map((a: any) => ({ ...a, title: decodeHtmlEntities(a.title || ''), excerpt: decodeHtmlEntities(a.excerpt || '') }));
  })();
  newsShared = { at: Date.now(), p };
  p.catch(() => { newsShared = null; });
  return p;
}

export interface PublicUser {
  id: string;
  displayName?: string;
  profileImageUrl?: string;
  accountType?: string;
  roles?: string[];
  genre?: string;
  bio?: string;
  slug?: string;
  [key: string]: any;
}

/**
 * Fetch public users for the home page Users section
 */
export async function getPublicUsers(maxItems = 20): Promise<PublicUser[]> {
  try {
    // Over-fetch then filter: unclaimed "ghost" contributors (and any hidden
    // users) are credited + searchable but must NOT surface on the homepage.
    const q = query(
      collection(db, 'users'),
      limit(maxItems * 5)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs
      .map(d => ({ id: d.id, ...d.data() } as PublicUser))
      .filter((u: any) =>
        u.accountType !== 'ghost' &&
        u.awaitingClaim !== true &&
        u.hidden !== true &&
        u.adminHidden !== true &&
        u.deleted !== true,
      )
      .slice(0, maxItems);
  } catch (error) {
    console.error('Error fetching public users:', error);
    return [];
  }
}
